import useStyles from 'isomorphic-style-loader/useStyles';
import type { HTMLAttributes, ReactNode } from 'react';

import { useContextPanel } from './context-panel-context';
import { ContextPanelOutlet } from './context-panel-outlet';
import { ContextPanelProvider } from './context-panel-provider';
import s from './context-panel-region.scss';

export interface ContextPanelRegionProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
}

/**
 * Internal component that reads from the component-scope context and renders
 * the panel alongside the main content in a flex-row layout.
 */
const ContextPanelRegionInner = ({
  children,
  ...rest
}: ContextPanelRegionProps) => {
  useStyles(s);

  const { isOpen, options } = useContextPanel('component');

  return (
    <section {...rest} className={s.root}>
      <div className={s.content}>{children}</div>

      <ContextPanelOutlet
        content={options?.content}
        header={options?.header}
        open={isOpen}
        padded={options?.padded}
        width={options?.width}
      />
    </section>
  );
};

/**
 * A layout wrapper for ad-hoc component-scope context panels. Provides the
 * component-scope `ContextPanelProvider` and renders a flex-row container with
 * the panel alongside the main content.
 *
 * The parent container must have a defined height for the panel to fill.
 *
 * @example
 * <ContextPanel.Region>
 *   <MyContent />
 * </ContextPanel.Region>
 *
 * // Then from inside MyContent:
 * const { open } = useContextPanel();
 */
export const ContextPanelRegion = ({
  children,
  ...rest
}: ContextPanelRegionProps) => (
  <ContextPanelProvider scope="component">
    <ContextPanelRegionInner {...rest}>{children}</ContextPanelRegionInner>
  </ContextPanelProvider>
);

export default ContextPanelRegion;
